<script>
import SettingsSection from '../../../../../../components/SettingsSection.vue';
import { useAlert } from 'dashboard/composables';
import EditReason from './EditReason.vue';
import CreateReason from './CreateReason.vue';

export default {
  components: {
    SettingsSection,
    EditReason,
    CreateReason,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      isInboxResolutionConfigUpdating: false,
      conversationResolutionIsEnabled: false,
      resolutionConfig: { conversation_resolution_with_reason: false },
      selectedConversationResolutionFlags: [],
      resolutionType: 'objective_achieved',
      selectedReason: {},
      showDeleteConfirmationPopup: false,
      showCreatePopup: false,
      showEditPopup: false,
      loadingDelete: false,
      loadingGeneratingOfDefaultOptions: false,
    };
  },
  computed: {
    resolutionReasons() {
      return this.inbox.conversation_resolution_reasons?.filter(
        e => e.resolution_type === this.resolutionType
      );
    },
    singleReasonInCurrentType() {
      return (
        this.resolutionReasons.filter(
          e => e.resolution_type === this.resolutionType
        ).length <= 1
      );
    },
    notAbleToActivateResolutionWithReason() {
      return (
        !this.inbox.conversation_resolution_reasons?.length &&
        this.conversationResolutionIsEnabled
      );
    },
  },
  watch: {
    inbox(newInbox) {
      this.setResolutionConfigInitialValues(newInbox.resolution_config);
    },
  },
  mounted() {
    this.setResolutionConfigInitialValues(this.inbox.resolution_config);
  },
  methods: {
    handleConversationResolutionFlag(e) {
      this.selectedConversationResolutionFlags = this.toggleInput(
        this.selectedConversationResolutionFlags,
        e.target.value
      );
      this.updateResolutionConfig();
    },
    toggleInput(selected, current) {
      if (selected.includes(current)) {
        const newSelectedFlags = selected.filter(flag => flag !== current);
        return newSelectedFlags;
      }
      return [...selected, current];
    },
    updateResolutionConfig() {
      if (!this.conversationResolutionIsEnabled) {
        this.resolutionConfig = { conversation_resolution_with_reason: false };
      } else {
        const [cpf_is_mandatory, set_label_is_mandatory] = [
          'cpf_is_mandatory',
          'set_label_is_mandatory',
        ].map(flag => this.selectedConversationResolutionFlags.includes(flag));

        this.resolutionConfig = {
          conversation_resolution_with_reason: true,
          options: { cpf_is_mandatory, set_label_is_mandatory },
        };
      }
    },
    setResolutionConfigInitialValues(resolutionCfgFromApi) {
      if (!resolutionCfgFromApi) return;

      if (resolutionCfgFromApi.conversation_resolution_with_reason)
        this.conversationResolutionIsEnabled =
          resolutionCfgFromApi.conversation_resolution_with_reason;

      if (resolutionCfgFromApi.options) {
        this.selectedConversationResolutionFlags = Object.keys(
          resolutionCfgFromApi.options
        ).filter(k => resolutionCfgFromApi.options[k]);
      }

      this.updateResolutionConfig();
    },
    async updateInbox() {
      try {
        this.isInboxResolutionConfigUpdating = true;
        const payload = {
          id: this.inbox.id,
          formData: false,
          resolution_config: this.resolutionConfig,
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.isInboxResolutionConfigUpdating = false;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      }
    },
    openEditPopup(reason) {
      this.selectedReason = reason;
      this.showEditPopup = true;
    },
    hideEditPopup() {
      this.selectedReason = {};
      this.showEditPopup = false;
    },
    openCreatePopup() {
      this.showCreatePopup = true;
    },
    hideCreatePopup() {
      this.showCreatePopup = false;
    },
    openDeletePopup(reason) {
      this.selectedReason = reason;
      this.showDeleteConfirmationPopup = true;
    },
    closeDeletePopup() {
      this.selectedReason = {};
      this.showDeleteConfirmationPopup = false;
    },
    async confirmDeletion() {
      this.loadingDelete = true;
      const payload = {
        inboxId: this.inbox.id,
        conversationResolutionId: this.selectedReason.id,
      };
      try {
        await this.$store.dispatch(
          'inboxes/destroyConversationResolutionReason',
          payload
        );
        this.showDeleteConfirmationPopup = false;
        useAlert(
          this.$t(
            'INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.REASONS.DELETE.CONFIRM.API.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        useAlert(
          this.$t(
            'INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.REASONS.DELETE.CONFIRM.API.ERROR_MESSAGE'
          )
        );
      }
      this.loadingDelete = false;
    },
    async generateDefaultOptionsForInbox() {
      this.loadingGeneratingOfDefaultOptions = true;
      const payload = { inboxId: this.inbox.id };
      try {
        await this.$store.dispatch(
          'inboxes/generateDefaultConversationResolutionReasons',
          payload
        );
        useAlert(
          this.$t(
            'INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.REASONS.CREATE.API.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        useAlert(
          this.$t(
            'INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.REASONS.CREATE.API.ERROR_MESSAGE'
          )
        );
      }
      this.loadingGeneratingOfDefaultOptions = false;
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.CONVERSATION_RESOLUTION')"
      :sub-title="
        $t('INBOX_MGMT.SETTINGS_POPUP.CONVERSATION_RESOLUTION_SUB_TEXT')
      "
    >
      <label class="medium-9 columns settings-item">
        {{
          $t('INBOX_MGMT.SETTINGS_POPUP.CONVERSATION_RESOLUTION_WITH_REASON')
        }}
        <select
          v-model="conversationResolutionIsEnabled"
          @change="updateResolutionConfig"
        >
          <option :value="true">
            {{ $t('INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.ENABLED') }}
          </option>
          <option :value="false">
            {{ $t('INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.DISABLED') }}
          </option>
        </select>
        <p class="pb-1 text-sm not-italic text-slate-600 dark:text-slate-400">
          {{
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.CONVERSATION_RESOLUTION_WITH_REASON_SUB_TEXT'
            )
          }}
        </p>
      </label>
      <label v-if="conversationResolutionIsEnabled">
        {{ $t('INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.OPTIONS_LABEL') }}
      </label>
      <div v-if="conversationResolutionIsEnabled" class="pt-2">
        <input
          v-model="selectedConversationResolutionFlags"
          type="checkbox"
          value="cpf_is_mandatory"
          @input="handleConversationResolutionFlag"
        />
        <label
          for="cpf_is_mandatory"
          class="text-xs inline pl-1 text-slate-600 dark:text-slate-400"
        >
          {{ $t('INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.FLAGS.CPF') }}
        </label>
      </div>
      <!-- The agent must set at least one label before resolve conversation -->
      <div v-if="conversationResolutionIsEnabled" class="pb-4">
        <input
          v-model="selectedConversationResolutionFlags"
          type="checkbox"
          value="set_label_is_mandatory"
          @input="handleConversationResolutionFlag"
        />
        <label
          for="set_label_is_mandatory"
          class="text-xs inline pl-1 text-slate-600 dark:text-slate-400"
        >
          {{ $t('INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.FLAGS.SET_LABEL') }}
        </label>
      </div>
      <woot-button
        v-tooltip.top="
          notAbleToActivateResolutionWithReason
            ? $t('INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.NOT_ABLE_TO_ACTIVATE')
            : ''
        "
        :disabled="notAbleToActivateResolutionWithReason"
        :loading="isInboxResolutionConfigUpdating"
        @click="updateInbox"
      >
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.UPDATE') }}
      </woot-button>

      <!-- REASONS SECTION -->
      <div v-if="conversationResolutionIsEnabled">
        <h5 class="text-slate-600 dark:text-slate-300 mt-5">
          {{ $t('INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.REASONS.HEADER') }}
        </h5>
        <div v-if="!inbox.conversation_resolution_reasons.length">
          <p class="text-xs">
            {{
              $t(
                'INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.REASONS.NO_RECORDS_WARNING'
              )
            }}
          </p>
          <woot-button
            variant="smooth"
            color-scheme="secondary"
            icon="wand"
            :is-loading="loadingGeneratingOfDefaultOptions"
            @click="generateDefaultOptionsForInbox"
          >
            {{
              $t(
                'INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.REASONS.CREATE.DEFAULT_OPTIONS'
              )
            }}
          </woot-button>
        </div>
        <label v-else class="medium-9 columns settings-item">
          {{
            $t(
              'INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.REASONS.RESOLUTION_TYPE'
            )
          }}
          <select v-model="resolutionType">
            <option value="objective_achieved">
              {{ $t('CONVERSATION.RESOLUTION_MODAL.OPTIONS.FIRST_LABEL') }}
            </option>
            <option value="objective_not_achieved">
              {{ $t('CONVERSATION.RESOLUTION_MODAL.OPTIONS.SECOND_LABEL') }}
            </option>
            <option value="doubt">
              {{ $t('CONVERSATION.RESOLUTION_MODAL.OPTIONS.THIRD_LABEL') }}
            </option>
            <option value="other">
              {{ $t('CONVERSATION.RESOLUTION_MODAL.OPTIONS.FOURTH_LABEL') }}
            </option>
          </select>
          <div>
            <woot-button
              v-if="conversationResolutionIsEnabled"
              variant="smooth"
              color-scheme="warning"
              icon="add-circle"
              @click="openCreatePopup"
            >
              {{
                $t(
                  'INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.REASONS.CREATE.BUTTON'
                )
              }}
            </woot-button>
          </div>
          <br />

          <span class="p-1">
            {{
              $t(
                'INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.REASONS.RECORDS_BY_TYPE'
              )
            }}
          </span>
          <hr class="m-1" />
          <table class="woot-table">
            <tbody>
              <tr v-for="reason in resolutionReasons" :key="reason.id">
                <td class="max-w-[24rem] min-w-[15rem]">
                  <p class="text-ellipsis">
                    {{ reason.title }}
                  </p>
                </td>
                <td class="button-wrapper">
                  <woot-button
                    v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
                    variant="smooth"
                    size="tiny"
                    color-scheme="secondary"
                    class-names="grey-btn"
                    icon="edit"
                    @click="openEditPopup(reason)"
                  />
                  <woot-button
                    v-tooltip.top="
                      singleReasonInCurrentType
                        ? $t(
                            'INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.REASONS.DELETE.TOOLTIP_SINGLE_OPTION'
                          )
                        : $t('LABEL_MGMT.FORM.DELETE')
                    "
                    :disabled="singleReasonInCurrentType"
                    variant="smooth"
                    color-scheme="alert"
                    size="tiny"
                    icon="dismiss-circle"
                    class-names="grey-btn"
                    @click="openDeletePopup(reason)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </label>
      </div>
    </SettingsSection>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditReason
        :reason="selectedReason"
        :inbox-id="inbox.id"
        :single-reason="singleReasonInCurrentType"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-modal v-model:show="showCreatePopup" :on-close="hideCreatePopup">
      <CreateReason
        :inbox-id="inbox.id"
        :initial-resolution-type="resolutionType"
        @close="hideCreatePopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="
        $t(
          'INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.REASONS.DELETE.CONFIRM.TITLE'
        )
      "
      :message="
        $t(
          'INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.REASONS.DELETE.CONFIRM.MESSAGE'
        )
      "
      message-value=""
      :confirm-text="
        $t('INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.REASONS.DELETE.CONFIRM.YES')
      "
      :reject-text="
        $t('INBOX_MGMT.EDIT.CONVERSATION_RESOLUTION.REASONS.DELETE.CONFIRM.NO')
      "
      :is-loading="loadingDelete"
    />
  </div>
</template>
